import { useState, type ChangeEvent, type FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';

import { Chip, Dialog, Icon, Input, LoadingButton, Tooltip } from '@myn/mui';

import { useUpdateVersionMutation } from '../../hooks/useBuyingPlanVersionMutation.js';
import {
  useCurrentVersionId,
  useUpdateBuyingPlanInfoCache,
} from '../../hooks/useGetBuyingPlanData.js';
import useSimpleNotification from '../../hooks/useSImpleNotification.js';
import {
  getCustomVersionName,
  getDefaultVersionName,
} from './utils/helpers.js';

type Props = {
  variant: 'active' | 'archived';
  versionId: number;
  name: string;
};

const getChipProps = (variant: 'active' | 'archived') => {
  if (variant === 'active') {
    return {
      variant: 'filled',
      size: 'medium',
      color: 'primary',
      icon: <Icon component="CheckmarkOutline" />,
    } as const;
  }

  return {
    variant: 'filled',
    size: 'medium',
    color: 'silver',
    icon: <Icon component="Archive" />,
  } as const;
};

const VersionName: FC<Props> = ({ variant, versionId, name }) => {
  const { t } = useTranslation('@myn/planning');
  const isReadOnlyAccess = !useIsAllowedPermission(
    'campaignPlanningFeature:modify',
  );
  const [isModalOpened, setIsModalOpened] = useState(false);

  const chipProps = getChipProps(variant);
  const defaultVersionName = getDefaultVersionName(name) ?? '';
  const customVersionName = getCustomVersionName(name) ?? '';

  const [localName, setLocalName] = useState(customVersionName);
  const { mutateAsync: updateVersion, isPending } = useUpdateVersionMutation();
  const { showSuccessNotification, showErrorNotification } =
    useSimpleNotification();
  const currentVersionId = useCurrentVersionId();
  const updateSelectedPlanCache = useUpdateBuyingPlanInfoCache();

  const previousName = customVersionName
    ? `${defaultVersionName} ${customVersionName}`
    : defaultVersionName;
  const localNameTrimmed = localName.trim();
  const currentName = localNameTrimmed
    ? `${defaultVersionName} ${localName}`
    : defaultVersionName;
  const isSameName = previousName === currentName;

  const resetNameToLastSavedValue = () => {
    setLocalName(customVersionName);
  };

  const handleOpenModal = () => {
    if (isReadOnlyAccess) {
      return;
    }

    setIsModalOpened(true);
  };

  const handleClose = () => {
    setIsModalOpened(false);
    resetNameToLastSavedValue();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLocalName(event.target.value);
  };

  const handleSaveName = async () => {
    try {
      await updateVersion({
        versionId,
        name: currentName,
      });
      showSuccessNotification({
        title: t('Success'),
        subTitle: t('Version name changed'),
      });
      setIsModalOpened(false);

      if (versionId === currentVersionId) {
        updateSelectedPlanCache({
          version: currentName,
        });
      }
    } catch {
      showErrorNotification({
        title: t('Error'),
        subTitle: t('Version name was not changed'),
      });
    }
  };

  return (
    <>
      <Tooltip title={customVersionName ? previousName : null} arrow={false}>
        <Chip {...chipProps} onClick={handleOpenModal} label={previousName} />
      </Tooltip>
      <Dialog
        open={isModalOpened}
        title={t('Change version name')}
        onClose={handleClose}
        submitButton={
          <LoadingButton
            size="large"
            variant="contained"
            loading={isPending}
            disabled={isSameName}
            onClick={handleSaveName}
          >
            {t('Save')}
          </LoadingButton>
        }
      >
        <Input
          fullWidth
          aria-label="Change version name"
          startAdornment={<Chip label={defaultVersionName} />}
          value={localName}
          onChange={handleChange}
        />
      </Dialog>
    </>
  );
};

export default VersionName;
